// core/modals/usermanagement/RoleModal.jsx
import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { userActions } from "../../action/userAction";
import { toast } from "react-toastify";

const RoleModal = ({ mode, role, onClose }) => {
    const [formData, setFormData] = useState({
        role_name: "",
        description: "",
        status: "active",
        permissions: []
    });

    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Load permissions on mount
    useEffect(() => {
        loadPermissions();
    }, []);

    // Load role data if in edit mode
    useEffect(() => {
        if (role && mode === 'edit') {
            setFormData({
                role_name: role.role_name || "",
                description: role.description || "",
                status: role.status || "active",
                permissions: []
            });

            // Load role permissions
            loadRolePermissions(role.id);
        }
    }, [role, mode]);

    // Load all permissions
    const loadPermissions = async () => {
        try {
            const result = await userActions.getPermissions();
            if (result.success) {
                setPermissions(result.data || {});
            } else {
                toast.error(result.message || 'Failed to load permissions');
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            toast.error('Error loading permissions');
        }
    };

    // Load role permissions
    const loadRolePermissions = async (roleId) => {
        try {
            const result = await userActions.getRolePermissions(roleId);
            if (result.success) {
                setFormData(prev => ({ ...prev, permissions: result.data || [] }));
            }
        } catch (error) {
            console.error('Error loading role permissions:', error);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Handle permission toggle
    const handlePermissionToggle = (permissionId) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(id => id !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions: newPermissions };
        });
    };

    // Handle select all permissions in a module
    const handleSelectAllInModule = (modulePermissions, checked) => {
        const moduleIds = modulePermissions.map(p => p.id);
        setFormData(prev => {
            if (checked) {
                // Add all module permissions not already in the list
                const newPermissions = [...new Set([...prev.permissions, ...moduleIds])];
                return { ...prev, permissions: newPermissions };
            } else {
                // Remove all module permissions
                const newPermissions = prev.permissions.filter(id => !moduleIds.includes(id));
                return { ...prev, permissions: newPermissions };
            }
        });
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.role_name.trim()) {
            newErrors.role_name = 'Role name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);

        const roleData = {
            role_name: formData.role_name,
            description: formData.description,
            status: formData.status,
            permissions: formData.permissions
        };

        if (mode === 'edit') {
            roleData.id = role.id;
        }

        try {
            let result;

            if (mode === 'add') {
                result = await userActions.createRole(roleData);
            } else {
                result = await userActions.updateRole(roleData);
            }

            console.log('Role operation result:', result);

            if (result && result.success === true) {
                toast.success(result.message || `Role ${mode === 'add' ? 'created' : 'updated'} successfully`);
                setTimeout(() => {
                    onClose(true);
                }, 1000);
            } else {
                toast.error(result?.message || `Failed to ${mode} role`);
            }
        } catch (error) {
            console.error(`Error ${mode}ing role:`, error);
            toast.error(error.message || `Failed to ${mode} role`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    // Check if all permissions in a module are selected
    const areAllModuleSelected = (modulePermissions) => {
        const moduleIds = modulePermissions.map(p => p.id);
        return moduleIds.length > 0 && moduleIds.every(id => formData.permissions.includes(id));
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {mode === 'add' ? 'Add Role' : 'Edit Role'}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Role Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="role_name"
                                        className={`form-control ${errors.role_name ? 'is-invalid' : ''}`}
                                        value={formData.role_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter role name"
                                        required
                                    />
                                    {errors.role_name && <div className="invalid-feedback">{errors.role_name}</div>}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Status</label>
                                    <select
                                        name="status"
                                        className="form-select"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                <div className="col-12 mt-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        name="description"
                                        className="form-control"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter role description"
                                        rows="2"
                                    />
                                </div>
                            </div>

                            <h6 className="fw-bold mb-3">Permissions</h6>

                            {Object.keys(permissions).length === 0 ? (
                                <div className="text-center py-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                Object.keys(permissions).map(module => (
                                    <div key={module} className="card mb-3">
                                        <div className="card-header bg-light">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0 text-capitalize">{module.replace('-', ' ')}</h6>
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`select-all-${module}`}
                                                        checked={areAllModuleSelected(permissions[module])}
                                                        onChange={(e) => handleSelectAllInModule(permissions[module], e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`select-all-${module}`}>
                                                        Select All
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <div className="row">
                                                {permissions[module].map(perm => (
                                                    <div key={perm.id} className="col-md-4 col-sm-6 mb-2">
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`perm-${perm.id}`}
                                                                checked={formData.permissions.includes(perm.id)}
                                                                onChange={() => handlePermissionToggle(perm.id)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`perm-${perm.id}`}>
                                                                {perm.permission_name}
                                                                <br />
                                                                <small className="text-muted">{perm.permission_slug}</small>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : mode === 'add' ? 'Create Role' : 'Update Role'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

RoleModal.propTypes = {
    mode: PropTypes.oneOf(['add', 'edit']).isRequired,
    role: PropTypes.object,
    onClose: PropTypes.func.isRequired
};

export default RoleModal;